import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import { HttpClient } from 'components/http/HttpClient';
import {
    getWorkingHoursParams,
    isLocationOpen as locationAvailabilityChecker
} from 'components/location/hooks/useLocationHelpers';
import { getLocalMerchantId } from 'components/settings/localStore';
import {
    getLocalAuthentication,
    resetLocalAuthentication,
    resetLocalToken,
    resetShouldUpdateGuestSession,
    resetUserid,
    setLocalAuthentication,
    updateLocalToken,
    updateUserId
} from 'components/user/localAuth';
import { SignUpFormData, User, UserAddress, UserCard, UserData } from 'components/user/model/User';
import { userApi } from 'components/user/userApi';
import logger from 'lib/logger';
import { isString } from 'lib/typeInference';
import type { ApplicationState } from 'store/store';

export interface AuthState {
    error: string;
    isLoading: boolean;
    isInitiallyAttempted: boolean;
    user?: User;
    token?: string;
    registerError?: string;
    activateError?: string;
    isRegistered: boolean;
    isActivated: boolean;
    id?: string;
    returnUrl?: string;
    card?: UserCard;
    isCardLoading?: boolean;
    showMarketing?: boolean;
}

const emptyErrors = {
    registerError: '',
    error: '',
    activateError: ''
};
const clearFlags = {
    isRegistered: false,
    isActivated: false
};

export const initialState: AuthState = {
    error: '',
    isLoading: false,
    isInitiallyAttempted: false,
    user: undefined,
    token: undefined,
    isRegistered: false,
    isActivated: false,
    isCardLoading: true
};

// Helper: compute session expiry from store state (replaces store.getState() anti-pattern)
const getSessionExpiryDate = (state: ApplicationState) => {
    const { currentLocation } = state.locations;
    const isLocationOpen = locationAvailabilityChecker(currentLocation?.openingHours);
    let sessionExpiryDate: moment.Moment | undefined;

    if (currentLocation && isLocationOpen && currentLocation.openingHours) {
        const { actualClosingTime } = getWorkingHoursParams(currentLocation.openingHours);
        sessionExpiryDate = actualClosingTime;
    } else {
        sessionExpiryDate = moment().add(1, 'days').startOf('day');
    }

    return sessionExpiryDate;
};

// Async thunks

export const register = createAsyncThunk(
    'auth/register',
    async (data: SignUpFormData, { rejectWithValue }) => {
        try {
            updateUserId(data.identity);
            try {
                const user = await userApi.createUser(data);
                updateLocalToken({ userId: user.id, token: user._id });
                return { type: 'success' as const, id: data.identity, user };
            } catch (err: any) {
                const error: AxiosResponse<{ message: string; code: string }> = err;
                if (error.status === 422) {
                    const { message } = error.data;
                    if (isString(message) && message.toUpperCase().includes('PENDING')) {
                        userApi.activateUser(data.identity);
                        return { type: 'pending' as const, id: data.identity };
                    }
                    throw new Error(message);
                }
            }
        } catch (e: any) {
            resetLocalToken();
            return rejectWithValue(e.message);
        }
    }
);

export const activate = createAsyncThunk(
    'auth/activate',
    async ({ id, token }: { id: string; token?: string }) => {
        const result = await userApi.activateUser(id, token);
        resetUserid();
        const merchantId = getLocalMerchantId();
        if (merchantId) {
            resetShouldUpdateGuestSession(merchantId);
        }
        HttpClient.token = result._id;
        setLocalAuthentication({
            userId: result.userId,
            token: result._id,
            isGuest: false,
            expiry: 0
        });
        return { token: result._id, user: result.user };
    }
);

export const getUserDetails = createAsyncThunk(
    'auth/getUserDetails',
    async ({ userId, noCache }: { userId: string; noCache?: boolean }) => {
        const localAuth = getLocalAuthentication();
        if (localAuth && !HttpClient.token) {
            HttpClient.token = localAuth.token;
        }
        const newUserDetails = await userApi.getUserDetails(userId, noCache);
        return newUserDetails;
    }
);

export const createGuestSession = createAsyncThunk(
    'auth/createGuestSession',
    async (_, { dispatch, getState, rejectWithValue }) => {
        try {
            const result = await userApi.getGuestSession();
            const sessionExpiry = getSessionExpiryDate(getState() as ApplicationState);

            setLocalAuthentication({
                userId: result.userId,
                token: result._id,
                isGuest: true,
                expiry: sessionExpiry?.unix()
            });

            HttpClient.token = result._id;

            dispatch(getUserDetails({ userId: result.userId }));
        } catch (err) {
            logger.info(err);
            return rejectWithValue(err);
        }
    }
);

export const signIn = createAsyncThunk(
    'auth/signIn',
    async (
        { credentials, token }: { credentials: string; token?: string },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const result = await (!!token
                ? userApi.loginWithToken(credentials, token)
                : userApi.loginUser(credentials));

            dispatch(finalizeSignIn(result));
            return result;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const googleSignIn = createAsyncThunk(
    'auth/googleSignIn',
    async (credential: string, { dispatch, rejectWithValue }) => {
        try {
            const result = await userApi.loginGoogleSSOUser(credential);
            const { status, ...rest } = result;
            if (status === 201) {
                dispatch(setShowMarketing(true));
            }
            dispatch(finalizeSignIn(rest));
            return result;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const logout = createAsyncThunk('auth/logout', async (id: string, { rejectWithValue }) => {
    try {
        if (!!id) {
            await userApi.logoutUser(id);
        }
        resetLocalAuthentication();
    } catch (e) {
        logger.error(e);
        resetLocalAuthentication();
        return rejectWithValue(e);
    }
});

export const getUserCard = createAsyncThunk(
    'auth/getUserCard',
    async (userId: string, { rejectWithValue }) => {
        try {
            const localAuth = getLocalAuthentication();
            if (localAuth && !HttpClient.token) {
                HttpClient.token = localAuth.token;
            }
            const newUserDetails = await userApi.getUserDetails(userId, true);
            return newUserDetails.card;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

// Slice

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearErrors(state) {
            Object.assign(state, emptyErrors);
        },
        setReturnUrl(state, action: PayloadAction<string>) {
            state.returnUrl = action.payload;
        },
        resetReturnUrl(state) {
            state.returnUrl = undefined;
        },
        updateUser(state, action: PayloadAction<User | undefined>) {
            if (!action.payload) {
                state.user = undefined;
                state.card = undefined;
                return;
            }
            state.user = {
                ...action.payload,
                credentials: action.payload?.credentials ?? state.user?.credentials ?? []
            } as any;
            state.card = action.payload?.card;
        },
        setShowMarketing(state, action: PayloadAction<boolean | undefined>) {
            state.showMarketing = !!action.payload;
        },
        setUserAddresses(state, action: PayloadAction<UserAddress[]>) {
            if (state.user) {
                state.user.addresses = action.payload as any;
            }
        },
        setRegisterError(state, action: PayloadAction<string>) {
            state.registerError = action.payload;
        },
        finalizeSignIn(state, action: PayloadAction<UserData>) {
            const userData = action.payload;
            HttpClient.token = userData._id;
            setLocalAuthentication({
                userId: userData.userId,
                token: userData._id,
                isGuest: false,
                expiry: 0
            });
            Object.assign(state, emptyErrors, clearFlags);
            state.token = userData._id;
            state.isInitiallyAttempted = true;
            state.user = userData.user as any;
            state.card = userData.user?.card;
            state.isLoading = false;
        }
    },
    extraReducers: builder => {
        builder
            // register
            .addCase(register.pending, state => {
                state.isLoading = true;
            })
            .addCase(register.fulfilled, (state, action) => {
                Object.assign(state, emptyErrors, clearFlags);
                if (action.payload?.type === 'pending') {
                    state.isRegistered = true;
                    state.id = action.payload.id;
                } else if (action.payload?.type === 'success') {
                    state.isRegistered = true;
                    state.id = action.payload.id;
                }
                state.isLoading = false;
            })
            .addCase(register.rejected, (state, action) => {
                Object.assign(state, emptyErrors, clearFlags);
                state.registerError = (action.payload as string) || action.error.message || '';
                state.isRegistered = false;
                state.id = undefined;
                state.isLoading = false;
            })
            // activate
            .addCase(activate.pending, state => {
                state.isLoading = true;
            })
            .addCase(activate.fulfilled, (state, action) => {
                Object.assign(state, emptyErrors, clearFlags);
                state.token = action.payload.token;
                state.isInitiallyAttempted = true;
                state.user = action.payload.user as any;
                state.card = action.payload.user?.card;
                state.isLoading = false;
            })
            .addCase(activate.rejected, state => {
                Object.assign(state, emptyErrors, clearFlags);
                state.activateError = 'Invalid activation code';
                state.isLoading = false;
            })
            // getUserDetails
            .addCase(getUserDetails.fulfilled, (state, action) => {
                state.user = action.payload as any;
                state.card = action.payload?.card;
            })
            .addCase(getUserDetails.rejected, state => {
                state.user = undefined;
                state.card = undefined;
            })
            // signIn
            .addCase(signIn.pending, state => {
                state.isLoading = true;
            })
            .addCase(signIn.rejected, state => {
                state.isLoading = false;
            })
            // googleSignIn
            .addCase(googleSignIn.rejected, state => {
                state.isLoading = false;
            })
            // logout
            .addCase(logout.fulfilled, state => {
                Object.assign(state, emptyErrors, clearFlags);
                state.token = undefined;
                state.user = undefined;
            })
            // getUserCard
            .addCase(getUserCard.pending, state => {
                state.isCardLoading = true;
            })
            .addCase(getUserCard.fulfilled, (state, action) => {
                state.card = action.payload;
                state.isCardLoading = false;
            })
            .addCase(getUserCard.rejected, state => {
                state.card = undefined;
                state.isCardLoading = false;
            });
    }
});

export const {
    clearErrors,
    setReturnUrl,
    resetReturnUrl,
    updateUser,
    setShowMarketing,
    setUserAddresses,
    setRegisterError,
    finalizeSignIn
} = authSlice.actions;

export default authSlice.reducer;
